// ─────────────────────────────────────────────────────────────────────────────
// StoreScene — full-game unlock screen
//
// Shows what the player gets for €4.99, handles purchase flow,
// and shows the TCG Collection Export code (bridge to Pazurumon TCG Online).
//
// init() data:
//   { returnTo: 'MapScene' | 'TitleScene', regionId?: string }
// ─────────────────────────────────────────────────────────────────────────────

import { PaymentService } from "../services/PaymentService.js";
import { UnlockManager }  from "../services/UnlockManager.js";
import { CloudSync }      from "../services/CloudSync.js";
import { Collection }     from "../game/collection.js";
import { createBackground } from "../vfx/background.js";
import { I18n }           from "../game/I18n.js";

export default class StoreScene extends Phaser.Scene {
  constructor() {
    super("StoreScene");
  }

  init(data) {
    this._returnTo  = data?.returnTo  || "MapScene";
    this._regionId  = data?.regionId  || "volcano";
    this._buying    = false;
  }

  create() {
    const W = this.scale.width;   // 540
    const H = this.scale.height;  // 960

    createBackground(this, "psychic"); // dark purple — feels premium

    this._drawHeader(W, H);
    this._drawFeatures(W, H);
    this._drawPriceBlock(W, H);
    this._drawActions(W, H);
    this._drawTCGBridge(W, H);
    this._drawFooter(W, H);
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Header — logo area + headline
  // ─────────────────────────────────────────────────────────────────────────
  _drawHeader(W, H) {
    // Top glow strip
    const g = this.add.graphics();
    g.fillGradientStyle(0x7c3aed, 0x7c3aed, 0x0b0f14, 0x0b0f14, 1);
    g.fillRect(0, 0, W, 180);
    g.setDepth(1);

    this.add.text(W / 2, 52, "PAZURUMON", {
      fontFamily: "Kamikaze, system-ui, Arial",
      fontSize:   "38px",
      color:      "#e9d5ff",
      fontStyle:  "bold",
    }).setOrigin(0.5).setDepth(2);

    this.add.text(W / 2, 96, I18n.t('store_subtitle'), {
      fontFamily: "system-ui, Arial",
      fontSize:   "13px",
      color:      "#a855f7",
      fontStyle:  "bold",
      letterSpacing: 4,
    }).setOrigin(0.5).setDepth(2);

    this.add.text(W / 2, 128, I18n.t('store_desc'), {
      fontFamily: "system-ui, Arial",
      fontSize:   "12px",
      color:      "#94a3b8",
      align:      "center",
      lineSpacing: 4,
    }).setOrigin(0.5).setDepth(2);

    // Divider
    this.add.rectangle(W / 2, 165, W * 0.6, 1, 0x7c3aed, 0.4).setDepth(2);
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Feature list
  // ─────────────────────────────────────────────────────────────────────────
  _drawFeatures(W, H) {
    const features = [
      { icon: "🌋", label: I18n.t('feature_volcano'),    sub: I18n.t('feature_r1_sub'),       free: true },
      { icon: "⚡", label: I18n.t('feature_storm'),      sub: I18n.t('feature_new_levels')  },
      { icon: "🌊", label: I18n.t('feature_ocean'),      sub: I18n.t('feature_new_levels')  },
      { icon: "🌿", label: I18n.t('feature_earth'),      sub: I18n.t('feature_new_levels')  },
      { icon: "🔮", label: I18n.t('feature_psychic'),    sub: I18n.t('feature_new_levels')  },
      { icon: "∞",  label: I18n.t('feature_infinite'),   sub: I18n.t('feature_infinite_sub')},
      { icon: "🏆", label: I18n.t('feature_badge'),      sub: I18n.t('feature_badge_sub')   },
      { icon: "🃏", label: I18n.t('feature_collection'), sub: I18n.t('feature_coll_sub'), highlight: true },
    ];

    const startY  = 188;
    const rowH    = 56;
    const padL    = 28;

    features.forEach((f, i) => {
      const y = startY + i * rowH;

      // Row background
      const rowColor = f.free ? 0x052010 : (f.highlight ? 0x1a0a3a : 0x0c1018);
      const rowBorder = f.free ? 0x16a34a : (f.highlight ? 0x7c3aed : 0x1e2d40);
      this.add.rectangle(W / 2, y + rowH / 2 - 4, W - 32, rowH - 6, rowColor, 0.92)
        .setStrokeStyle(1, rowBorder, f.free || f.highlight ? 0.7 : 0.3)
        .setDepth(3);

      // Icon
      this.add.text(padL + 2, y + rowH / 2 - 2, f.icon, {
        fontSize: "22px",
      }).setOrigin(0, 0.5).setDepth(4);

      // Label
      const labelColor = f.free ? "#4ade80" : (f.highlight ? "#c4b5fd" : "#e2e8f0");
      this.add.text(padL + 40, y + rowH / 2 - 9, f.label, {
        fontFamily: "system-ui, Arial",
        fontSize:   "13px",
        color:      labelColor,
        fontStyle:  "bold",
      }).setOrigin(0, 0.5).setDepth(4);

      // Sub label
      this.add.text(padL + 40, y + rowH / 2 + 8, f.sub, {
        fontFamily: "system-ui, Arial",
        fontSize:   "10px",
        color:      f.free ? "#4ade80" : "#64748b",
      }).setOrigin(0, 0.5).setDepth(4);

      // Checkmark / FREE badge
      const badgeLabel = f.free ? I18n.t('badge_free') : "✓";
      const badgeColor = f.free ? "#4ade80" : (f.highlight ? "#a78bfa" : "#7c3aed");
      this.add.text(W - padL, y + rowH / 2 - 4, badgeLabel, {
        fontFamily: "system-ui, Arial",
        fontSize:   f.free ? "10px" : "16px",
        color:      badgeColor,
        fontStyle:  "bold",
      }).setOrigin(1, 0.5).setDepth(4);
    });
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Price block
  // ─────────────────────────────────────────────────────────────────────────
  _drawPriceBlock(W, H) {
    const y = 652;

    this.add.rectangle(W / 2, y, W - 32, 64, 0x1a0a3a, 0.97)
      .setStrokeStyle(2, 0x7c3aed, 0.9)
      .setDepth(3);

    this.add.text(W / 2 - 10, y, UnlockManager.getPriceDisplay(), {
      fontFamily: "Kamikaze, system-ui, Arial",
      fontSize:   "42px",
      color:      "#f5d0fe",
      fontStyle:  "bold",
    }).setOrigin(0.5).setDepth(4);

    this.add.text(W / 2 + 60, y - 6, I18n.t('payment_once'), {
      fontFamily: "system-ui, Arial",
      fontSize:   "10px",
      color:      "#94a3b8",
      lineSpacing: 3,
    }).setOrigin(0, 0.5).setDepth(4);
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Action buttons — Buy & Back
  // ─────────────────────────────────────────────────────────────────────────
  _drawActions(W, H) {
    const buyY = 730;

    // ── Buy button ───────────────────────────────────────────────────────
    const buyBg = this.add.rectangle(W / 2, buyY, W - 60, 56, 0x7c3aed, 1)
      .setStrokeStyle(2, 0xd8b4fe, 0.6)
      .setDepth(5)
      .setInteractive({ useHandCursor: true });

    this._buyLabel = this.add.text(W / 2, buyY, I18n.t('unlock_btn', { price: UnlockManager.getPriceDisplay() }), {
      fontFamily: "system-ui, Arial",
      fontSize:   "14px",
      color:      "#ffffff",
      fontStyle:  "bold",
    }).setOrigin(0.5).setDepth(6);

    // Pulse glow on buy button
    this.tweens.add({
      targets:  buyBg,
      scaleX:   1.02,
      scaleY:   1.02,
      duration: 1200,
      yoyo:     true,
      repeat:   -1,
      ease:     "Sine.InOut",
    });

    buyBg.on("pointerover", () => buyBg.setFillStyle(0x9333ea, 1));
    buyBg.on("pointerout",  () => buyBg.setFillStyle(0x7c3aed, 1));
    buyBg.on("pointerdown", () => this._onBuy(buyBg));

    // ── Restore link ─────────────────────────────────────────────────────
    const restore = this.add.text(W / 2, buyY + 42, I18n.t('restore_purchase'), {
      fontFamily: "system-ui, Arial",
      fontSize:   "11px",
      color:      "#64748b",
    }).setOrigin(0.5).setDepth(5).setInteractive({ useHandCursor: true });

    restore.on("pointerover", () => restore.setColor("#94a3b8"));
    restore.on("pointerout",  () => restore.setColor("#64748b"));
    restore.on("pointerdown", () => this._onRestore());

    // ── Back button ───────────────────────────────────────────────────────
    const back = this.add.text(W / 2, buyY + 68, I18n.t('btn_back'), {
      fontFamily: "system-ui, Arial",
      fontSize:   "11px",
      color:      "#475569",
    }).setOrigin(0.5).setDepth(5).setInteractive({ useHandCursor: true });

    back.on("pointerover", () => back.setColor("#94a3b8"));
    back.on("pointerout",  () => back.setColor("#475569"));
    back.on("pointerdown", () => this._goBack());
  }

  // ─────────────────────────────────────────────────────────────────────────
  // TCG Bridge — collection export code
  // ─────────────────────────────────────────────────────────────────────────
  _drawTCGBridge(W, H) {
    const y = 828;

    this.add.rectangle(W / 2, y, W - 32, 80, 0x0f172a, 0.96)
      .setStrokeStyle(1, 0x312e81, 0.6)
      .setDepth(3);

    this.add.text(W / 2, y - 22, I18n.t('tcg_header'), {
      fontFamily: "system-ui, Arial",
      fontSize:   "11px",
      color:      "#818cf8",
      fontStyle:  "bold",
    }).setOrigin(0.5).setDepth(4);

    this.add.text(W / 2, y - 4, I18n.t('tcg_desc'), {
      fontFamily: "system-ui, Arial",
      fontSize:   "10px",
      color:      "#475569",
      align:      "center",
      lineSpacing: 3,
    }).setOrigin(0.5).setDepth(4);

    // Show player ID (short form) as proof of identity
    const pid   = CloudSync.getPlayerId();
    const short = pid.slice(0, 8).toUpperCase();

    this.add.text(W / 2, y + 26, I18n.t('collector_id', { id: short }), {
      fontFamily: "system-ui, Arial",
      fontSize:   "10px",
      color:      "#4f46e5",
      fontStyle:  "bold",
    }).setOrigin(0.5).setDepth(4);
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Footer
  // ─────────────────────────────────────────────────────────────────────────
  _drawFooter(W, H) {
    this.add.text(W / 2, H - 12, I18n.t('store_footer'), {
      fontFamily: "system-ui, Arial",
      fontSize:   "8px",
      color:      "#1e293b",
    }).setOrigin(0.5).setDepth(2);
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Handlers
  // ─────────────────────────────────────────────────────────────────────────
  async _onBuy(buyBg) {
    if (this._buying) return;
    this._buying = true;

    this._buyLabel.setText(I18n.t('processing'));
    buyBg.setInteractive(false);

    const result = await UnlockManager.purchase();

    if (result.success) {
      // grantUnlock() already called inside UnlockManager.purchase()
      this._showSuccessOverlay();
    } else if (result.pending) {
      // Stripe: new tab / Steam: overlay
      this._buyLabel.setText(I18n.t('payment_pending'));
      this._showToast(I18n.t('payment_open_tab'));
      this._buying = false;
      buyBg.setInteractive({ useHandCursor: true });
    } else {
      this._buyLabel.setText(I18n.t('unlock_btn', { price: UnlockManager.getPriceDisplay() }));
      this._showToast(result.error || I18n.t('payment_error'));
      this._buying = false;
      buyBg.setInteractive({ useHandCursor: true });
    }
  }

  async _onRestore() {
    const result = await UnlockManager.restore();
    if (result.success) {
      this._showSuccessOverlay();
    } else {
      this._showToast(I18n.t('restore_not_found'));
    }
  }

  _goBack() {
    this.scene.start(this._returnTo, { regionId: this._regionId });
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Success overlay — shown after purchase
  // ─────────────────────────────────────────────────────────────────────────
  _showSuccessOverlay() {
    const W = this.scale.width;
    const H = this.scale.height;
    const D = 200;

    const overlay = this.add.rectangle(W / 2, H / 2, W, H, 0x000000, 0.88)
      .setDepth(D).setInteractive();

    this.add.rectangle(W / 2, H / 2, W - 48, 340, 0x0b0f14, 0.98)
      .setStrokeStyle(2, 0x7c3aed, 0.9)
      .setDepth(D + 1);

    this.add.text(W / 2, H / 2 - 110, "✓", {
      fontSize:  "56px",
      color:     "#4ade80",
    }).setOrigin(0.5).setDepth(D + 2);

    this.add.text(W / 2, H / 2 - 46, I18n.t('game_unlocked_title'), {
      fontFamily: "system-ui, Arial",
      fontSize:   "20px",
      color:      "#f5d0fe",
      fontStyle:  "bold",
    }).setOrigin(0.5).setDepth(D + 2);

    this.add.text(W / 2, H / 2 + 4, I18n.t('game_unlocked_msg'), {
      fontFamily: "system-ui, Arial",
      fontSize:   "13px",
      color:      "#94a3b8",
      align:      "center",
      lineSpacing: 5,
    }).setOrigin(0.5).setDepth(D + 2);

    // Collection code for TCG
    const col   = Collection.load();
    const pid   = CloudSync.getPlayerId();
    const short = pid.slice(0, 8).toUpperCase();
    this.add.text(W / 2, H / 2 + 78, I18n.t('tcg_id_cards', { id: short, n: col.cards.length }), {
      fontFamily: "system-ui, Arial",
      fontSize:   "11px",
      color:      "#818cf8",
    }).setOrigin(0.5).setDepth(D + 2);

    const btn = this.add.text(W / 2, H / 2 + 128, I18n.t('start_adventure'), {
      fontFamily: "system-ui, Arial",
      fontSize:   "14px",
      color:      "#a855f7",
      fontStyle:  "bold",
    }).setOrigin(0.5).setDepth(D + 2).setInteractive({ useHandCursor: true });

    btn.on("pointerover", () => btn.setColor("#ffffff"));
    btn.on("pointerout",  () => btn.setColor("#a855f7"));
    btn.on("pointerdown", () => this._goBack());
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Toast
  // ─────────────────────────────────────────────────────────────────────────
  _showToast(msg) {
    const W     = this.scale.width;
    const toast = this.add.text(W / 2, 110, msg, {
      fontFamily:      "system-ui, Arial",
      fontSize:        "12px",
      color:           "#f1f5f9",
      backgroundColor: "#1a2540",
      padding:         { x: 14, y: 8 },
    }).setOrigin(0.5).setAlpha(0).setDepth(300);

    this.tweens.add({
      targets:  toast,
      alpha:    1,
      duration: 200,
      yoyo:     true,
      hold:     2200,
      ease:     "Sine.InOut",
      onComplete: () => toast.destroy(),
    });
  }
}
